import React, { useEffect, useState } from "react";
import { collection, onSnapshot, query, orderBy, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { Link, useNavigate } from "react-router-dom";
import "./community.css";
import Reactions from "../community/Reactions";

/* ==========================================================
   CATEGORY DICTIONARY — unified for AI + UI
   ========================================================== */
const TOPICS = [
  { id: "strategies-tips", label: "Strategies & Tips", icon: "💡" },
  { id: "interview-tips", label: "Interview Tips", icon: "🎤" },
  { id: "success-stories", label: "Success Stories", icon: "🏆" },
  { id: "salary-transparency", label: "Salary Transparency", icon: "💰" },
  { id: "remote-work", label: "Remote Work & Freelancing", icon: "🌍" },
  { id: "resume-reviews", label: "Resume Reviews", icon: "📄" },
  { id: "tech-careers", label: "Tech Careers", icon: "💻" },
  { id: "breaking-into-careers", label: "Breaking Into New Careers", icon: "🎯" },
  { id: "workplace-issues", label: "Workplace Issues", icon: "👥" },
  { id: "networking-opportunities", label: "Networking & Opportunities", icon: "🤝" },
];

/* Utility */
const now = () => Date.now()

/* ==========================================================
   COMPONENT
   ========================================================== */
function CommunityHome() {
  const [posts, setPosts] = useState([]);
  // ==========================================================
// PINNED POSTS (derived, zero-downtime)
// ==========================================================
const pinnedPosts = posts.filter(p => p.isPinned === true);
const regularPosts = posts
  .filter(p => p.isPinned !== true)
  .sort((a, b) => b.createdAt - a.createdAt);

pinnedPosts.sort(
  (a, b) => (a.pinPriority ?? 999) - (b.pinPriority ?? 999)
);

const orderedPosts = [...pinnedPosts, ...regularPosts];

  const [topicCounts, setTopicCounts] = useState({});
  const [trending, setTrending] = useState([]);
  const [activeThreads, setActiveThreads] = useState([]);



  /* ==========================================================
     1️⃣ LIVE: All Posts + Category Counts
     ========================================================== */
  useEffect(() => {
    const q = query(
  collection(db, "community_posts"),
  orderBy("createdAt", "desc")
);

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const allPosts = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          reactions: doc.data().reactions || {
            like: 0,
            celebrate: 0,
            insightful: 0,
            support: 0,
            funny: 0,
          },
        }));

        setPosts(allPosts);

        const counts = {};
        allPosts.forEach((p) => {
          counts[p.category] = (counts[p.category] || 0) + 1;
        });
        setTopicCounts(counts);
      },
      (err) => {
        console.error("Community snapshot error:", err);
      }
    );

    return () => unsubscribe();
  }, []);

  /* ==========================================================
     2️⃣ Trending Topics (last 7 days)
     ========================================================== */
  useEffect(() => {
    const sevenDays = now() - 7 * 24 * 60 * 60 * 1000;
    const trendingCalc = {};

    posts.forEach((post) => {
      if (post.createdAt >= sevenDays) {
        trendingCalc[post.category] =
          (trendingCalc[post.category] || 0) + 1;
      }
    });

    const sorted = Object.entries(trendingCalc)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([cat]) => cat);

    setTrending(sorted);
  }, [posts]);

  /* ==========================================================
     3️⃣ Latest Active Threads (updatedAt)
     ========================================================== */
  useEffect(() => {
    if (!posts.length) return;

    const nowMs = Date.now()
    const sevenDaysAgo = nowMs - 7 * 24 * 60 * 60 * 1000;
    const trendingCalc = {};

    posts.forEach((post) => {
      let created = post.createdAt;

      if (typeof created === "string") created = Number(created);
      if (created?._seconds) created = created._seconds * 1000;
      if (!created || isNaN(created)) return;

      const isRecent =
        created >= sevenDaysAgo ||
        created > nowMs ||
        nowMs - created <= 45 * 24 * 60 * 60 * 1000;

      if (isRecent) {
        trendingCalc[post.category] =
          (trendingCalc[post.category] || 0) + 1;
      }
    });

    const sorted = Object.entries(trendingCalc)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([cat]) => cat);

    setTrending(sorted);
  }, [posts]);

  /* ==========================================================
     UI
     ========================================================== */
  return (
    <div className="forum-wrapper">
      <h1 className="forum-title">Community Discussions</h1>

      {/* Create New Post */}
      <Link to="/community/new-post">
        <button className="newpost-btn">+ Create New Post</button>
      </Link>

      {/* TRENDING SECTION */}
      {trending.length > 0 && (
        <div className="trending-box">
          <h2 className="topic-header">🔥 Trending This Week</h2>
          <div className="trending-list">
            {trending.map((cat) => {
              const topic = TOPICS.find((t) => t.id === cat);
              if (!topic) return null;

              return (
                <Link
                  key={cat}
                  to={`/community/topic/${cat}`}
                  className="trending-item"
                >
                  {topic.icon} {topic.label}
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* AI Tools Section */}
      <div className="forum-card" style={{ marginTop: "25px" }}>
        <h3 className="forum-section-title">🤖 AI Career Tools</h3>
        <p style={{ marginBottom: "15px", color: "#555" }}>
          Boost your career with professional AI-powered tools.
        </p>

        <div className="ai-tool-grid">
          <a href="/community/tools/resume-critique" className="ai-tool-btn">
            📄 Resume Critique
          </a>

          <a href="/community/tools/interview-coach" className="ai-tool-btn">
            🎙 Interview Coach
          </a>

          <a href="/community/tools/career-advisor" className="ai-tool-btn">
            📊 Career Advisor
          </a>

          <a href="/community/tools/linkedin-generator" className="ai-tool-btn">
            💼 Profile Builder (LinkedIn-optimized)
          </a>

          <a href="/community/tools/cover-letter-generator" className="ai-tool-btn">
            ✉️ Cover Letter Generator
          </a>
        </div>
      </div>

      {/* TOPIC LIST */}
      <div className="topic-section">
        <h2 className="topic-header">Browse Topics</h2>
        <div className="topic-list">
          {TOPICS.map((t) => (
            <Link
              key={t.id}
              to={`/community/topic/${t.id}`}
              className="topic-item"
            >
              <div className="topic-left">
                <span className="topic-icon">{t.icon}</span>
                <span>{t.label}</span>
              </div>
              <span className="topic-count">
                {topicCounts[t.id] || 0}
              </span>
            </Link>
          ))}
        </div>
      </div>

      {/* ACTIVE THREADS */}
      <div className="active-box">
        <h2 className="topic-header">💬 Latest Activity</h2>
        <div className="active-list">
          {activeThreads.map((t) => (
            <Link
              key={t.id}
              to={`/community/thread/${t.id}`}
              className="active-item"
            >
              <strong>{t.title}</strong>
              <span className="active-meta">
                {new Date(t.updatedAt).toLocaleDateString()}
              </span>
            </Link>
          ))}
        </div>
      </div>

      {/* ALL POSTS */}
      <div className="forum-list">
        {orderedPosts.length === 0 ? (
          <p className="empty-msg">
            No posts yet — be the first to share!
          </p>
        ) : (
          orderedPosts.map((post) => (
            <div key={post.id} className="forum-card">
  <Link to={`/community/thread/${post.id}`} className="forum-link">
    <div className="forum-card-top">
      <span className="forum-category">
        {TOPICS.find((t) => t.id === post.category)?.label ||
          post.category}
      </span>
      <p className="post-meta">
       Posted by {post.username} •
       {post.createdAt
       ? new Date(
      post.createdAt.seconds
        ? post.createdAt.seconds * 1000
        : post.createdAt
        ).toLocaleDateString()
        : "Just now"}
      </p>
    </div>

    <h3 className="forum-card-title">
      {post.isPinned && <span style={{ marginRight: "6px" }}>📌</span>}
      {post.title}
    </h3>

    <p className="forum-card-body">
      {post.body.length > 140
        ? post.body.substring(0, 140) + "..."
        : post.body}
    </p>
  </Link>

  <p className="forum-user">
    Posted by: {post.username || "User"}
    {post.location ? ` • ${post.location}` : ""}
  </p>

  <Reactions postId={post.id} post={post} />
</div>
          ))
        )}
      </div>
    </div>
  );
}

export default CommunityHome;



